import React, { useState } from 'react';
import Button from '../../../shared/components/ui/Button';
import Modal from '../../../shared/components/ui/Modal';

const BulkEngineerAssignmentModal = ({
  isOpen,
  onClose,
  workTypes,
  allUsers,
  tasks,
  onAssignEngineers,
  isSubmitting = false,
  getUserColor,
  preSelectedWorkType = null, // New prop for pre-selected work type
  showAllTasksInitially = true, // New prop to show all tasks initially
  setIsSubmitting
}) => {
  // Local state for the modal
  const [selectedWorkType, setSelectedWorkType] = useState('');
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectedEngineers, setSelectedEngineers] = useState([]);
  const [autoSelectedEngineers, setAutoSelectedEngineers] = useState([]); // Track auto-selected engineers
  const [hasManualEngineerSelection, setHasManualEngineerSelection] = useState(false); // Track if engineers were manually selected

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setSelectedWorkType(preSelectedWorkType || '');
      setSelectedTasks([]);
      setSelectedEngineers([]);
      setAutoSelectedEngineers([]);
      setHasManualEngineerSelection(false);
    }
  }, [isOpen, preSelectedWorkType]);

  const handleWorkTypeSelection = (workTypeId) => {
    setSelectedWorkType(workTypeId);
    setSelectedTasks([]); // Reset tasks when work type changes
    setSelectedEngineers([]); // Reset engineer selection when work type changes
    setAutoSelectedEngineers([]); // Reset auto-selected engineers when work type changes
    setHasManualEngineerSelection(false); // Reset manual selection tracking when work type changes
  };

  const getTasksByWorkType = (workTypeId) => {
    // If showAllTasksInitially is true and no workTypeId is selected, show all tasks
    if (showAllTasksInitially && !workTypeId) {
      return tasks || [];
    }
    // If workTypeId is provided, filter by work type
    if (!workTypeId) return [];
    return tasks.filter((task) => task.workType_id === Number(workTypeId));
  };

  // Function to find and auto-select engineers common to all selected tasks
  const updateCommonEngineers = (selectedTaskIds) => {
    // Don't auto-select if engineers were manually selected
    if (hasManualEngineerSelection) {
      return;
    }

    if (selectedTaskIds.length === 0) {
      // No tasks selected, clear engineer selection
      setSelectedEngineers([]);
      setAutoSelectedEngineers([]);
      return;
    }

    // Get all selected task objects
    const selectedTaskObjects = tasks.filter((task) => selectedTaskIds.includes(task.id));

    if (selectedTaskObjects.length === 0) {
      setSelectedEngineers([]);
      setAutoSelectedEngineers([]);
      return;
    }

    // Find engineers who are assigned to ALL selected tasks
    const commonEngineerIds = selectedTaskObjects.reduce((commonEngineers, task, index) => {
      // Get engineer IDs assigned to this task from engineerLiveTaskMapping
      const taskEngineerIds = (task.engineerLiveTaskMapping || [])
        .filter((mapping) => {
          // Check if the user has a valid level (Medium or Low)
          const userLevel = mapping.user?.level;
          return userLevel === 'Medium' || userLevel === 'Low';
        })
        .map((mapping) => mapping.user.id);

      console.log(`Task ${task.id} (${task.name}) has engineers:`, taskEngineerIds);

      if (index === 0) {
        // First task - start with all its engineers
        return taskEngineerIds;
      } else {
        // Subsequent tasks - keep only engineers that are also in this task
        const filtered = commonEngineers.filter((engineerId) =>
          taskEngineerIds.includes(engineerId)
        );
        console.log(`Common engineers after task ${task.id}:`, filtered);
        return filtered;
      }
    }, []);

    console.log('Final common engineer IDs:', commonEngineerIds);

    // Validate the common engineers against the 1 Medium + 1 Low rule
    const commonEngineersData = allUsers.filter((user) => commonEngineerIds.includes(user.user.id));

    const mediumLevelEngineers = commonEngineersData.filter(
      (user) => user.user?.level === 'Medium'
    );
    const lowLevelEngineers = commonEngineersData.filter((user) => user.user?.level === 'Low');

    // Only auto-select if it doesn't violate the rules (max 1 Medium + 1 Low)
    if (mediumLevelEngineers.length <= 1 && lowLevelEngineers.length <= 1) {
      setSelectedEngineers(commonEngineerIds);
      setAutoSelectedEngineers(commonEngineerIds); // Track which engineers were auto-selected
    } else {
      // If there are too many common engineers, don't auto-select to avoid rule violations
      setSelectedEngineers([]);
      setAutoSelectedEngineers([]);
    }
  };

  const handleTaskSelection = (taskId, checked) => {
    if (checked) {
      setSelectedTasks((prev) => {
        const newSelectedTasks = [...prev, taskId];
        // Auto-select engineers common to all selected tasks
        updateCommonEngineers(newSelectedTasks);
        return newSelectedTasks;
      });
    } else {
      setSelectedTasks((prev) => {
        const newSelectedTasks = prev.filter((id) => id !== taskId);
        // Update common engineers when task is deselected
        updateCommonEngineers(newSelectedTasks);
        return newSelectedTasks;
      });
    }
  };

  const handleSelectAllTasks = (checked) => {
    if (checked) {
      // Get tasks based on current filter (could be all tasks or filtered by work type)
      const currentTasks = showAllTasksInitially
        ? getTasksByWorkType(selectedWorkType)
        : getTasksByWorkType(selectedWorkType);
      const allTaskIds = currentTasks.map((task) => task.id);
      setSelectedTasks(allTaskIds);
      // Auto-select engineers common to all selected tasks
      updateCommonEngineers(allTaskIds);
    } else {
      setSelectedTasks([]);
      // Clear engineer selection only if it was auto-selected, not manually selected
      if (!hasManualEngineerSelection) {
        setSelectedEngineers([]);
        setAutoSelectedEngineers([]);
      }
    }
  };

  const handleEngineerSelection = (engineerId, checked) => {
    if (checked) {
      // Validate engineer level selection
      const engineer = allUsers.find((user) => user.user.id === engineerId);
      if (engineer && engineer.user?.level) {
        const currentSelectedEngineers = allUsers.filter((user) =>
          selectedEngineers.includes(user.user.id)
        );
        const mediumLevelCount = currentSelectedEngineers.filter(
          (user) => user.user?.level === 'Medium'
        ).length;
        const lowLevelCount = currentSelectedEngineers.filter(
          (user) => user.user?.level === 'Low'
        ).length;

        if (engineer.user.level === 'Medium' && mediumLevelCount >= 1) {
          alert('Only one Manager can be selected at a time.');
          return;
        }
        if (engineer.user.level === 'Low' && lowLevelCount >= 1) {
          alert('Only one Executive can be selected at a time.');
          return;
        }
      }
      setSelectedEngineers((prev) => [...prev, engineerId]);
    } else {
      setSelectedEngineers((prev) => prev.filter((id) => id !== engineerId));
    }

    // Mark that engineers were manually selected and clear auto-selected tracking
    setHasManualEngineerSelection(true);
    setAutoSelectedEngineers([]);
  };

  const handleBulkAssignment = async () => {
    // Validate selections
    if (selectedTasks.length === 0) {
      alert('Please select at least one task.');
      return;
    }

    if (selectedEngineers.length === 0) {
      alert('Please select at least one engineer.');
      return;
    }

    // Validate engineer level selection
    const selectedEngineerData = allUsers.filter((user) =>
      selectedEngineers.includes(user.user.id)
    );
    const mediumLevelEngineers = selectedEngineerData.filter(
      (user) => user.user.level === 'Medium'
    );
    const lowLevelEngineers = selectedEngineerData.filter((user) => user.user.level === 'Low');

    if (mediumLevelEngineers.length !== 1) {
      alert('Please select exactly one Manager.');
      return;
    }

    if (lowLevelEngineers.length > 1) {
      alert('Only one Executive can be selected.');
      return;
    }

    setIsSubmitting(true);
    // Call the parent's assignment handler
    await onAssignEngineers({
      selectedTasks,
      selectedEngineers,
      selectedWorkType
    });

    // Reset state after successful assignment
    setSelectedWorkType('');
    setSelectedTasks([]);
    setSelectedEngineers([]);
    setAutoSelectedEngineers([]);
    setHasManualEngineerSelection(false);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    setSelectedWorkType('');
    setSelectedTasks([]);
    setSelectedEngineers([]);
    setAutoSelectedEngineers([]);
    setHasManualEngineerSelection(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal isDirty={true} width={80} name="Assign Engineers to Tasks" onClose={handleClose}>
      <div className="flex flex-col gap-4">
        {/* Two Column Layout: Work Type + Tasks Left, Engineers Right */}
        <div className="flex gap-6">
          {/* Left Column: Work Type and Tasks */}
          <div className="flex-1">
            {/* Work Type Selection - Show as filter when showAllTasksInitially is true, or as required when not pre-selected */}
            {!preSelectedWorkType && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {showAllTasksInitially ? 'Filter by Work Type (Optional)' : 'Select Work Type'}
                  {!showAllTasksInitially && <span className="text-red-500"> *</span>}
                </label>
                <select
                  value={selectedWorkType}
                  onChange={(e) => handleWorkTypeSelection(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
                  <option value="" disabled={!showAllTasksInitially}>
                    {showAllTasksInitially ? 'All Work Types' : 'Select Work Type'}
                  </option>
                  {workTypes.map((workType) => (
                    <option key={workType.id} value={workType.value}>
                      {workType.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Tasks Selection - Show when work type is selected OR when showAllTasksInitially is true */}
            {(selectedWorkType || showAllTasksInitially) && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Select Tasks <span className="text-red-500">*</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={(() => {
                        const currentTasks = showAllTasksInitially
                          ? getTasksByWorkType(selectedWorkType)
                          : getTasksByWorkType(selectedWorkType);
                        return (
                          selectedTasks.length === currentTasks.length && currentTasks.length > 0
                        );
                      })()}
                      onChange={(e) => handleSelectAllTasks(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    Select All
                  </label>
                </div>
                <div className="border border-gray-200 rounded-md max-h-80 overflow-y-auto">
                  {(() => {
                    const currentTasks = showAllTasksInitially
                      ? getTasksByWorkType(selectedWorkType)
                      : getTasksByWorkType(selectedWorkType);

                    return currentTasks.length > 0 ? (
                      currentTasks.map((task) => (
                        <label
                          key={task.id}
                          className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                          <input
                            type="checkbox"
                            checked={selectedTasks.includes(task.id)}
                            onChange={(e) => handleTaskSelection(task.id, e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{task.name}</div>
                            <div className="text-sm text-gray-500">Status: {task.status}</div>
                          </div>
                        </label>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500">
                        {showAllTasksInitially && !selectedWorkType
                          ? 'No tasks available'
                          : 'No tasks found for this work type'}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Engineers Selection - Always visible */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Engineers <span className="text-red-500">*</span>
              <span className="text-xs text-gray-500 ml-2">(Required: 1 Manager · Executive optional)</span>
            </label>
            <div className="border border-gray-200 rounded-md max-h-96 overflow-y-auto">
              {allUsers.map((user) => {
                const isSelected = selectedEngineers.includes(user.user.id);
                const currentSelectedEngineers = allUsers.filter((u) =>
                  selectedEngineers.includes(u.user.id)
                );
                const mediumLevelCount = currentSelectedEngineers.filter(
                  (u) => u.user?.level === 'Medium'
                ).length;
                const lowLevelCount = currentSelectedEngineers.filter(
                  (u) => u.user?.level === 'Low'
                ).length;

                let isDisabled = false;
                if (!isSelected && user.user?.level) {
                  if (user.user.level === 'Medium' && mediumLevelCount >= 1) {
                    isDisabled = true;
                  }
                  if (user.user.level === 'Low' && lowLevelCount >= 1) {
                    isDisabled = true;
                  }
                }

                const initials = user.user?.name
                  .split(' ')
                  .map((word) => word[0])
                  .join('')
                  .toUpperCase();

                return (
                  <label
                    key={user.user.id}
                    className={`flex items-center gap-3 p-3 border-b border-gray-100 last:border-b-0 ${isDisabled
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-gray-50 cursor-pointer'
                      } ${isSelected ? 'bg-blue-50' : ''}`}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={(e) => handleEngineerSelection(user.user.id, e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                    />
                    <div
                      className={`w-8 h-8 rounded-full text-white flex items-center justify-center font-semibold text-sm ${getUserColor(user.user.id)}`}>
                      {initials}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-gray-900">{user.user?.name}</div>
                        {autoSelectedEngineers.includes(user.user.id) && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            pre-selected
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {user.user?.level === 'Medium' ? 'Manager' : 'Executive'}
                      </div>
                    </div>
                    {isDisabled && <div className="text-xs text-red-500">Max reached</div>}
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
          <Button displayType="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkAssignment}
            disabled={selectedTasks.length === 0 || selectedEngineers.length === 0 || isSubmitting}>
            Assign Engineers
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkEngineerAssignmentModal;
